<template>
    <div class="chat-list">
        <ul v-if="hasItems" class="chat-list__items" role="list">
            <li v-for="item in items" :id="getChatRoomElementId(item)" :key="getItemKey(item)" class="chat-list__item"
                :class="{
                    'chat-list__item--active':
                        isSelectedChatRoom(item)
                }">
                <button type="button" class="chat-list__content" :class="{
                    'chat-list__content--with-call':
                        isIndividualChat(item)
                }" :title="getChatTitle(item)" @click="selectChatRoom(item)">
                    <UserAvatar v-if="item.is_group_chat" :is-group="true" :image="item.group_image || ''"
                        :name="getGroupName(item)" />

                    <UserAvatar v-else-if="item.user" :image="getUserThumbnail(item.user)"
                        :is-online="Boolean(item.user.is_online)" :current-status="item.user.current_status || ''
                            " :name="getUserName(item.user)" />

                    <div class="chat-list__info">
                        <p v-if="!collapseActive" class="chat-list__name">
                            <span class="chat-list__name-text">
                                {{ getChatTitle(item) }}
                            </span>

                            <span v-if="hasUnreadMessages(item)" class="chat-list__badge" :aria-label="getUnreadAriaLabel(item)
                                ">
                                {{ getUnreadCount(item) }}
                            </span>
                        </p>

                       <span
                            v-if="isTyping(item)"
                            class="chat-list__typing"
                            aria-live="polite"
                        >
                            <template
                                v-if="
                                    item.is_group_chat &&
                                    !collapseActive &&
                                    typingFirstName
                                "
                            >
                                {{ typingFirstName }} is typing...
                            </template>

                            <template v-else>
                                Typing...
                            </template>
                        </span>

                        <!-- <p
                            v-else-if="getLastMessage(item)"
                            class="chat-list__last-message"
                        >
                            {{ getLastMessage(item) }}
                        </p> -->
                    </div>

                    <div v-if="shouldShowStatus(item) && !collapseActive" class="chat-list__status-tooltip" role="tooltip">
                        <span class="
                                chat-list__status-tooltip-title
                            ">
                            {{ getCurrentStatus(item.user) }}
                        </span>

                        <span v-if="
                            getLastSeenTime(
                                item.user.status_time
                            )
                        " class="
                                chat-list__status-tooltip-time
                            ">
                            {{
                                getLastSeenTime(
                                    item.user.status_time
                                )
                            }}
                        </span>
                    </div>
                </button>

                <VideoCallButton v-if="isIndividualChat(item) && !collapseActive" class="chat-list__video-call"
                    :is-online="Boolean(item.user.is_online)" :browser-online="Boolean(item.user.browser_online)
                        " :active="isSameId(
                        activeCallUserId,
                        item.user.id
                    )
                        " :is-calling="isCalling" @call="startVideoCall(item)" />
            </li>
        </ul>

        <template v-if="listType === 'search'">
            <div v-if="!searchResultLoaded" class="chat-list__empty" role="status">
                {{ searchingText }}
            </div>

            <div v-else-if="!hasItems" class="chat-list__empty">
                No chats or contacts found.
            </div>
        </template>
    </div>
</template>

<script>
const UserAvatar = () =>
    import(
        '@/components/private/workspace/ui/UserAvatar'
    );

const VideoCallButton = () =>
    import(
        '@/components/private/workspace/ui/VideoCallButton'
    );

export default {
    name: 'UserSearchChatList',

    components: {
        UserAvatar,
        VideoCallButton
    },

    props: {
        items: {
            type: Array,
            default: function () {
                return [];
            }
        },

        listType: {
            type: String,
            default: 'normal',
            validator: function (value) {
                return [
                    'normal',
                    'pinned',
                    'search'
                ].indexOf(value) !== -1;
            }
        },

        searchResultLoaded: {
            type: Boolean,
            default: false
        },

        searchingText: {
            type: String,
            default: 'Looking for contact'
        },

        typingUserInfo: {
            type: [Object, String],
            default: ''
        },

        selectedChatRoom: {
            type: Object,
            default: null
        },

        collapseActive: {
            type: Boolean,
            default: false
        },

        activeCallUserId: {
            type: [Number, String],
            default: null
        },

        isCalling: {
            type: Boolean,
            default: false
        }
    },

    computed: {
        hasItems() {
            return this.items.length > 0;
        },
        typingFirstName() {
            if (
                !this.typingUserInfo ||
                typeof this.typingUserInfo !== 'object'
            ) {
                return '';
            }

            const typingUser =
                this.typingUserInfo.typing_user;

            return typingUser &&
                typingUser.first_name
                ? typingUser.first_name
                : '';
        },

        typingRoomId() {
            if (
                !this.typingUserInfo ||
                typeof this.typingUserInfo !== 'object'
            ) {
                return null;
            }

            return (
                this.typingUserInfo.ChatRoomId ||
                this.typingUserInfo.chat_room ||
                this.typingUserInfo.room_id ||
                null
            );
        }
    },

    methods: {
        isIndividualChat(item) {
            return Boolean(
                item &&
                !item.is_group_chat &&
                item.user
            );
        },

        getItemKey(item) {
            if (!item) {
                return 'unknown-chat';
            }

            return (
                item.chat_room ||
                item.id ||
                (
                    item.user &&
                    'user-' + item.user.id
                ) ||
                'unknown-chat'
            );
        },

        getChatRoomElementId(item) {
            return (
                'chatRoomId' +
                String(
                    item &&
                    (
                        item.chat_room ||
                        item.id ||
                        ''
                    )
                )
            );
        },

        getGroupName(item) {
            if (!item) {
                return 'Group';
            }

            return (
                item.name ||
                item.group_name ||
                'Group'
            );
        },

        getChatTitle(item) {
            if (!item) {
                return '';
            }

            if (item.is_group_chat) {
                return this.getGroupName(item);
            }

            if (item.user) {
                return this.getUserName(item.user);
            }

            return 'Unknown chat';
        },

        getLastMessage(item) {
            if (
                !item ||
                !item.last_message
            ) {
                return '';
            }

            if (
                typeof item.last_message === 'string'
            ) {
                return item.last_message;
            }

            return (
                item.last_message.message ||
                item.last_message.text ||
                ''
            );
        },

        getCurrentStatus(user) {
            if (!user) {
                return '';
            }

            if (user.current_status) {
                return user.current_status;
            }

            return user.is_online
                ? 'Available'
                : 'Offline';
        },

        shouldShowStatus(item) {
            if (!this.isIndividualChat(item)) {
                return false;
            }

            const status =
                this.getCurrentStatus(item.user)
                    .toLowerCase();

            return status !== 'available';
        },

        getLastSeenTime(time) {
            if (!time) {
                return '';
            }

            const date = new Date(time);
            const timestamp = date.getTime();

            if (Number.isNaN(timestamp)) {
                return '';
            }

            const difference =
                Date.now() - timestamp;

            if (difference < 0) {
                return '';
            }

            const totalMinutes =
                Math.floor(difference / 60000);

            if (totalMinutes < 1) {
                return 'Last seen just now';
            }

            if (totalMinutes < 60) {
                return (
                    'Last seen ' +
                    totalMinutes +
                    'm ago'
                );
            }

            const totalHours =
                Math.floor(totalMinutes / 60);

            if (totalHours < 24) {
                const minutes =
                    totalMinutes % 60;

                return minutes
                    ? (
                        'Last seen ' +
                        totalHours +
                        'h ' +
                        minutes +
                        'm ago'
                    )
                    : (
                        'Last seen ' +
                        totalHours +
                        'h ago'
                    );
            }

            const days =
                Math.floor(totalHours / 24);

            return (
                'Last seen ' +
                days +
                (days === 1 ? ' day ago' : ' days ago')
            );
        },

        isSelectedChatRoom(item) {
            if (
                !item ||
                !this.selectedChatRoom
            ) {
                return false;
            }

            const itemRoomId =
                item.chat_room || item.id;

            const selectedRoomId =
                this.selectedChatRoom.chat_room ||
                this.selectedChatRoom.ChatRoomId ||
                this.selectedChatRoom.id;

            return this.isSameId(
                itemRoomId,
                selectedRoomId
            );
        },

        isSameId(firstId, secondId) {
            if (
                firstId === null ||
                firstId === undefined ||
                secondId === null ||
                secondId === undefined
            ) {
                return false;
            }

            return String(firstId) === String(secondId);
        },

        getUserThumbnail(user) {
            if (
                !user ||
                !user.profile
            ) {
                return '';
            }

            return user.profile.thumbnail || '';
        },

        getUserName(user) {
            if (!user) {
                return 'Guest';
            }

            const fullName = [
                user.first_name || '',
                user.last_name || ''
            ]
                .join(' ')
                .trim();

            return (
                fullName ||
                user.email ||
                'Guest'
            );
        },

        isTyping(item) {
            if (
                !item ||
                this.typingRoomId === null
            ) {
                return false;
            }

            return this.isSameId(
                item.chat_room,
                this.typingRoomId
            );
        },

        hasUnreadMessages(item) {
            return this.getRawUnreadCount(item) > 0;
        },

        getRawUnreadCount(item) {
            if (!item) {
                return 0;
            }

            const unreadCount =
                Number(item.unread_count);

            return Number.isFinite(unreadCount)
                ? Math.max(0, unreadCount)
                : 0;
        },

        getUnreadCount(item) {
            const count =
                this.getRawUnreadCount(item);

            return count > 99 ? '99+' : count;
        },

        getUnreadAriaLabel(item) {
            const count =
                this.getRawUnreadCount(item);

            return (
                count +
                (
                    count === 1
                        ? ' unread message'
                        : ' unread messages'
                )
            );
        },

        selectChatRoom(item) {
            if (!item) {
                return;
            }

            this.$emit(
                'select-chat-room',
                item
            );
        },

        startVideoCall(item) {
            if (
                !item ||
                !item.user ||
                this.isCalling
            ) {
                return;
            }

            this.$emit('video-call', {
                userId: item.user.id,
                roomId: item.chat_room,
                item: item
            });
        }
    }
};
</script>

<style lang="scss" scoped>
$background-hover: #181c1f;
$background-active: #1d2226;
$text-primary: #ffffff;
$text-secondary: #999999;
$badge-background: #87cefa;
$typing-color: #00de09;
$tooltip-background: #ffdee3;
$tooltip-text: #000000;

.chat-list {
    &__items {
        margin: 0;
        padding: 0;
        list-style: none;
    }

    &__item {
        position: relative;
        padding:8px 10px;
        border-right: 5px solid transparent;
        transition: background-color 0.2s ease;

        &:hover,
        &:focus-within {
            background-color: $background-hover;

            .chat-list__status-tooltip {
                opacity: 1;
                visibility: visible;
                transform: translateY(0);
            }
        }

       @media only screen and (min-width:768px){
         &--active {
            background-color: $background-active;
        }
       }
    }

    &__content {
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;
        min-width: 0;
        padding: 0;
        border: 0;
        color: inherit;
        background: transparent;
        font: inherit;
        text-align: left;
        cursor: pointer;

        &--with-call {
            padding-right: 50px;
        }

        &:focus {
            outline: 2px solid $badge-background;
            outline-offset: 3px;
        }
    }

    &__info {
        flex: 1;
        min-width: 0;
    }

    &__name {
        display: flex;
        align-items: center;
        min-width: 0;
        margin: 0;
        font-size: 18px;
        line-height: 1.3;
    }

    &__name-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__badge {
        flex: 0 0 auto;
        min-width: 20px;
        height: 20px;
        margin-left: 6px;
        padding: 0 5px;
        border-radius: 10px;
        color: #000000;
        background-color: $badge-background;
        font-size: 11px;
        font-weight: 600;
        line-height: 20px;
        text-align: center;
    }

    &__last-message {
        margin:0px;
        overflow: hidden;
        color: $text-secondary;
        font-size: 14px;
        line-height: 1.3;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__typing {
        display: block;
        color: $typing-color;
        font-family:
            'MyriadProRegular',
            'Lato',
            sans-serif;
        font-size: 14px;
        line-height: 1.3;
    }

    &__video-call {
        position: absolute;
        top: 50%;
        right: 5px;
        z-index: 2;
        transform: translateY(-50%);
    }

    &__status-tooltip {
        position: absolute;
        top: 0;
        right: 45px;
        z-index: 10;
        max-width: 220px;
        padding: 6px 10px;
        border-radius: 3px;
        color: $tooltip-text;
        background-color: $tooltip-background;
        font-family:
            'MyriadProRegular',
            'Lato',
            sans-serif;
        pointer-events: none;
        opacity: 0;
        visibility: hidden;
        transform: translateY(-4px);
        transition:
            opacity 0.2s ease,
            visibility 0.2s ease,
            transform 0.2s ease;
    }

    &__status-tooltip-title,
    &__status-tooltip-time {
        display: block;
        line-height: 1.2;
    }

    &__status-tooltip-title {
        margin-bottom: 3px;
        font-size: 14px;
    }

    &__status-tooltip-time {
        color: #5c5c5c;
        font-size: 13px;
    }

    &__empty {
        padding: 50px 15px 0;
        color: $text-secondary;
        font-size: 18px;
        text-align: center;
    }
}

@media only screen and (min-width: 768px) and (max-width: 1310px) {
    .chat-list {
        &__name {
            font-size: 16px;
        }
    }
}
</style>